import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LIFE_PLAN_COURSE_PATH } from 'utils/lifePlanNavigation';
import { Check, ChevronDown, Clock3, Info, LockKeyhole, MoreHorizontal, RefreshCw, RotateCcw, X } from 'lucide-react';

import { Icon } from 'components/icons/Icon';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import {
  snapshot,
  type SnapshotContentItem,
  type SnapshotGoal,
  type SnapshotGoalInput,
  type SnapshotProgressUpdate,
  type SnapshotGoalStatus,
  type SnapshotPayload,
} from 'services/api';
import {
  requestSnapshotGoalCheckIn,
  subscribeToSnapshotGoalUpdates,
} from 'utils/snapshotGoalUpdates';

import { GoalLifecycleModal } from './GoalLifecycleModal';
import { GoalPlanEditor } from './GoalPlanEditor';
import { GoalProgressModal, type GoalProgressConfirmation } from './GoalProgressModal';
import { SnapshotContentEditor } from './SnapshotContentEditor';
import { SnapshotRadar } from './SnapshotRadar';
import { SnapshotContentGate, SnapshotLock, snapshotGoalsUnlocked, snapshotItemUnlocked } from './SnapshotLock';
import './Snapshot.css';

const formatDate = (value?: string | null) => {
  if (!value) return 'not yet';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const parsed = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'not yet';
  return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(parsed);
};

const scrollTo = (id: string) => {
  const element = document.getElementById(id);
  if (!element || typeof element.scrollIntoView !== 'function') return;
  const reducedMotion = typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  element.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
};
const localDate = (timezone: string) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
};
const successorInput = (goal: SnapshotGoal): SnapshotGoalInput => ({
  title: goal.title,
  motivation: goal.motivation,
  next_action: goal.next_action,
  goal_type: goal.goal_type || 'recurring',
  tracking_config: JSON.parse(JSON.stringify(goal.tracking_config)),
  timezone: goal.timezone,
  start_date: localDate(goal.timezone),
  check_in_interval_days: 1,
});
const mergeGoalIntoSnapshot = (current: SnapshotPayload, goal: SnapshotGoal): SnapshotPayload => {
  const goalExists = current.goals.some((item) => item.id === goal.id);
  const goals = goal.status === 'retired'
    ? current.goals.filter((item) => item.id !== goal.id)
    : goalExists
      ? current.goals.map((item) => item.id === goal.id ? goal : item)
      : [...current.goals, goal];
  const dueGoalIds = goal.is_due
    ? current.due_goal_ids.includes(goal.id)
      ? current.due_goal_ids
      : [...current.due_goal_ids, goal.id]
    : current.due_goal_ids.filter((id) => id !== goal.id);
  return { ...current, goals, due_goal_ids: dueGoalIds };
};
const newIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
};
const sentenceCase = (value: string) => value
  ? `${value.charAt(0).toUpperCase()}${value.slice(1)}`
  : 'Progress';
const reachedWeeklyTarget = (before: SnapshotGoal, after: SnapshotGoal) => {
  if (before.progress.kind !== 'recurring' || after.progress.kind !== 'recurring') return false;
  if (after.progress.period_unit !== 'week') return false;
  if (!before.progress.periods.current.counts_toward_goal) return false;
  if (before.progress.periods.current.target_ever_reached) return false;
  return before.progress.current_period_value < before.progress.target_per_period
    && after.progress.current_period_value >= after.progress.target_per_period;
};

interface QuickGoalConfirmation extends GoalProgressConfirmation {
  weeklyTargetReached: boolean;
  goalTargetReached: boolean;
}
const textValue = (item?: SnapshotContentItem) => {
  const value = item?.value;
  return typeof value === 'string' ? value : '';
};
const listValue = (item?: SnapshotContentItem) => {
  const value = item?.value;
  return Array.isArray(value) ? value.filter((entry) => entry.trim()) : [];
};
const labeledValue = (item?: SnapshotContentItem) => {
  const value = item?.display_value;
  return Array.isArray(value) ? value as { label: string; value: string }[] : [];
};

const MiniRadar = ({ data }: { data: SnapshotPayload['radar'] }) => {
  if (!data) return null;
  const count = data.axes.length;
  const hasRatings = data.axes.some((axis) => axis.latest_value !== null);
  const centerX = 120; const centerY = 120; const radius = 86; const max = data.definition.scale_max;
  const pointsFor = (factor: (index: number) => number) => Array.from({ length: count }, (_, index) => {
    const angle = -Math.PI / 2 + (Math.PI * 2 * index) / count;
    const distance = radius * factor(index);
    return `${centerX + Math.cos(angle) * distance},${centerY + Math.sin(angle) * distance}`;
  }).join(' ');
  if (!hasRatings) return <div className="snapshot-collage-radar-empty">
    <svg viewBox="0 0 240 240" aria-hidden="true">
      {[.25,.5,.75,1].map((ring) => <polygon key={ring} points={pointsFor(() => ring)} fill="none" stroke="rgba(106,9,221,.18)" strokeWidth="1.4" strokeDasharray="4 5" />)}
    </svg>
    <p>Rate your pillars to see the shape of this season.</p>
  </div>;
  const scores = pointsFor((index) => (data.axes[index].latest_value || 0) / max);
  return <svg viewBox="0 0 240 240" className="snapshot-collage-radar-svg" role="img" aria-label={`Compact pillar ratings: ${data.axes.map((axis) => `${axis.label} ${axis.latest_value ?? 'not rated'}`).join(', ')}`}>
    {[.25,.5,.75,1].map((ring) => <polygon key={ring} points={pointsFor(() => ring)} fill="none" stroke="rgba(106,9,221,.14)" />)}
    {Array.from({ length: count }, (_, index) => <line key={index} x1={centerX} y1={centerY} x2={pointsFor((point) => point === index ? 1 : 0).split(' ')[index].split(',')[0]} y2={pointsFor((point) => point === index ? 1 : 0).split(' ')[index].split(',')[1]} stroke="rgba(106,9,221,.1)" />)}
    <polygon points={scores} fill="rgba(106,9,221,.10)" stroke="rgba(106,9,221,.85)" strokeWidth="2.5" strokeLinejoin="round" />
  </svg>;
};

const Empty = ({ children }: { children: string }) => (
  <div className="snapshot-empty"><p>{children}</p></div>
);

const SectionHeader = ({
  headingId,
  number,
  title,
  action,
}: {
  headingId: string;
  number: string;
  title: string;
  action?: ReactNode;
}) => <div className="snapshot-section-heading">
  <div className="snapshot-section-heading__title">
    <span aria-hidden="true">{number}</span>
    <h2 id={headingId} className="snapshot-heading">{title}</h2>
  </div>
  {action}
</div>;

const SectionEditMenu = ({
  label,
  items,
  onEdit,
}: {
  label: string;
  items: SnapshotContentItem[];
  onEdit: (item: SnapshotContentItem) => void;
}) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
      <Button type="button" variant="secondary" size="auto" className="snapshot-section-edit">Edit this section</Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent side="bottom" align="end" className="min-w-60 border border-foundation-calm-blue-100 bg-white" aria-label={label}>
      {items.filter(Boolean).map((item) => (
        <DropdownMenuItem key={item.key} className="min-h-11" disabled={!snapshotItemUnlocked(item)} onSelect={() => onEdit(item)}>{item.title}{!snapshotItemUnlocked(item) ? <><LockKeyhole className="ml-auto h-4 w-4" aria-hidden="true" /><span className="sr-only"> — Locked</span></> : null}</DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

const SnapshotPageHeader = ({
  season,
  updatedLine,
  progress,
}: {
  season?: string;
  updatedLine: ReactNode;
  progress?: SnapshotPayload['content_progress'];
}) => <header className="snapshot-page-header">
  <div>
    <p className="snapshot-season">Your season{season ? ` · ${season}` : ''}</p>
    <h1 className="snapshot-title">Your Snapshot</h1>
    <p className="snapshot-intro">A living view of where you are, where you want to go, and the steps you’re taking to get there.</p>
    <p className="snapshot-updated">{updatedLine}</p>
  </div>
  {progress ? <p className="snapshot-content-progress" aria-label="Life Plan reflection progress"><strong>{progress.answered}<span> / {progress.total}</span></strong><span>reflections answered</span></p> : null}
</header>;

const ExpandableText = ({
  text,
  expanded,
  onToggle,
  className = '',
  label,
}: {
  text: string;
  expanded: boolean;
  onToggle: () => void;
  className?: string;
  label: string;
}) => (
  <>
    <p className={`${className} snapshot-expandable-text ${expanded ? 'snapshot-expandable-text--open' : ''}`}>{text}</p>
    <button type="button" className="snapshot-read-more" aria-expanded={expanded} onClick={onToggle}>
      {expanded ? 'Show less' : label}
    </button>
  </>
);

type GoalProgressMode = 'quick' | 'correct';

const plural = (value: number, singular: string, pluralValue = `${singular}s`) => value === 1 ? singular : pluralValue;

const goalSummary = (goal: SnapshotGoal) => {
  const progress = goal.progress;
  if (progress.kind === 'recurring') {
    const streak = progress.consecutive
      ? ` · ${progress.current_streak}-${progress.period_unit} streak`
      : '';
    return `${progress.current_period_value} of ${progress.target_per_period} this ${progress.period_unit}${streak}`;
  }
  if (progress.kind === 'count' || progress.kind === 'quantity') return `${progress.value} of ${progress.target} ${progress.unit}`;
  if (progress.kind === 'milestone' || progress.kind === 'deadline') return `${progress.completed_count} of ${progress.steps.length} ${plural(progress.steps.length, 'step')}`;
  if (progress.kind === 'reflective') {
    return progress.target
      ? `${progress.total_count} of ${progress.target} reflections`
      : `${progress.total_count} reflections · ${progress.month_count} this month`;
  }
  return 'Tracking setup incomplete';
};

const compactDate = (value?: string | null) => formatDate(value).replace(/, \d{4}$/, '');

const daysBetweenDates = (start: string, end: string) => {
  const toUtc = (value: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    return match ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
  };
  const startValue = toUtc(start);
  const endValue = toUtc(end);
  if (startValue === null || endValue === null) return null;
  return Math.round((endValue - startValue) / 86_400_000);
};

const compactGoalSummary = (goal: SnapshotGoal) => {
  const progress = goal.progress;
  let summary = goalSummary(goal);
  if (progress.kind === 'recurring') {
    summary = progress.current_period_value + ' of ' + progress.target_per_period + ' ' + progress.unit + ' this ' + progress.period_unit;
    if (progress.required_periods) {
      summary += ' · ' + progress.qualifying_periods + ' of ' + progress.required_periods + ' ' + plural(progress.required_periods, progress.period_unit);
    }
  } else if (progress.kind === 'deadline' && progress.target_date) {
    const remaining = daysBetweenDates(localDate(goal.timezone), progress.target_date);
    if (remaining !== null) {
      summary += remaining < 0
        ? ' · ' + Math.abs(remaining) + ' ' + plural(Math.abs(remaining), 'day') + ' overdue'
        : remaining === 0
          ? ' · due today'
          : ' · ' + remaining + ' ' + plural(remaining, 'day') + ' left';
    }
  }

  if (goal.status === 'paused') return summary + ' · paused ' + compactDate(goal.last_updated_at);
  if (!goal.can_accept_progress) return 'Tracking opens ' + compactDate(goal.progress_available_on) + ' · ' + summary;
  if (progress.target_reached) return summary + ' · target reached ' + compactDate(goal.last_updated_at);
  return summary;
};

const compactGoalSummaryLabel = (goal: SnapshotGoal) => {
  const details = [compactGoalSummary(goal)];
  if (goal.progress.kind === 'deadline' && goal.progress.target_date) {
    details.push('Deadline ' + formatDate(goal.progress.target_date));
  }
  if (!goal.can_accept_progress) details.push('Tracking opens ' + formatDate(goal.progress_available_on));
  return details.join('. ');
};

const goalState = (goal: SnapshotGoal) => {
  if (goal.status === 'paused') return { label: 'Paused', className: 'snapshot-state--paused', glyph: 'Ⅱ' };
  if (goal.status === 'completed') return { label: 'Complete', className: 'snapshot-state--complete', glyph: '✓' };
  if (!goal.can_accept_progress) return { label: 'Scheduled', className: 'snapshot-state--scheduled', glyph: '◷' };
  if (goal.progress.target_reached) return { label: 'Target reached', className: 'snapshot-state--complete', glyph: '✓' };
  return { label: 'Active', className: 'snapshot-state--active', glyph: '●' };
};

const CompactGoalProgress = ({ goal, celebrating }: { goal: SnapshotGoal; celebrating: boolean }) => {
  const progress = goal.progress;
  const renderSlots = (value: number, target: number, label: string) => {
    if (target > 8) {
      const percent = Math.max(0, Math.min(100, (value / target) * 100));
      return (
        <div className="snapshot-compact-progress__bar" role="progressbar" aria-valuemin={0} aria-valuemax={target} aria-valuenow={Math.min(value, target)} aria-valuetext={label}>
          <span style={{ width: percent + '%' }} />
        </div>
      );
    }
    return (
      <div className="snapshot-compact-progress__slots" role="img" aria-label={label}>
        {Array.from({ length: target }, (_, index) => {
          const complete = index < value;
          const justCompleted = celebrating && index === Math.min(value, target) - 1;
          return <span key={index} data-testid={complete ? 'snapshot-compact-completion' : undefined} className={[complete ? 'is-complete' : '', justCompleted ? 'is-new' : ''].filter(Boolean).join(' ')} aria-hidden="true">{complete ? <Check /> : null}</span>;
        })}
      </div>
    );
  };

  if (progress.kind === 'recurring') {
    return renderSlots(
      progress.current_period_value,
      progress.target_per_period,
      progress.current_period_value + ' of ' + progress.target_per_period + ' ' + progress.unit + ' this ' + progress.period_unit,
    );
  }
  if (progress.kind === 'reflective' && progress.target) {
    return renderSlots(progress.total_count, progress.target, progress.total_count + ' of ' + progress.target + ' reflections complete');
  }
  if (progress.kind === 'milestone' || progress.kind === 'deadline') {
    return renderSlots(progress.completed_count, progress.steps.length, progress.completed_count + ' of ' + progress.steps.length + ' steps complete');
  }
  if (progress.kind === 'count' || progress.kind === 'quantity') {
    const percent = Math.max(0, Math.min(100, (progress.value / progress.target) * 100));
    return (
      <div className="snapshot-compact-progress__bar" role="progressbar" aria-valuemin={0} aria-valuemax={progress.target} aria-valuenow={Math.min(progress.value, progress.target)} aria-valuetext={progress.value + ' of ' + progress.target + ' ' + progress.unit}>
        <span style={{ width: percent + '%' }} />
      </div>
    );
  }
  return null;
};

const goalTrackingPlan = (goal: SnapshotGoal) => {
  const progress = goal.progress;
  if (progress.kind === 'recurring') {
    return `${progress.target_per_period} ${progress.unit} per ${progress.period_unit} for ${progress.required_periods} ${plural(progress.required_periods, progress.period_unit)}${progress.consecutive ? ', consecutively' : ''}`;
  }
  if (progress.kind === 'count' || progress.kind === 'quantity') {
    return `${progress.target} ${progress.unit} total`;
  }
  if (progress.kind === 'milestone') {
    return `${progress.steps.length} ordered ${plural(progress.steps.length, 'milestone')}`;
  }
  if (progress.kind === 'deadline') {
    return `${progress.steps.length} ${plural(progress.steps.length, 'step')} by ${formatDate(progress.target_date)}`;
  }
  if (progress.kind === 'reflective') {
    return progress.target
      ? `${progress.target} ${plural(progress.target, 'reflection')} to finish`
      : 'Ongoing reflection practice';
  }
  return 'Tracking setup is not complete yet';
};

const goalCheckInCadence = (days: number | null) => {
  if (!days) return 'Check-in cadence not set';
  return days === 1 ? 'Daily check-in' : `Check in every ${days} days`;
};

const GoalDetailMeter = ({ value, target, label }: { value: number; target: number; label: string }) => {
  const safeTarget = Math.max(target, 1);
  const percent = Math.max(0, Math.min(100, (value / safeTarget) * 100));
  return (
    <div
      className="snapshot-goal-details__meter"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={safeTarget}
      aria-valuenow={Math.min(value, safeTarget)}
      aria-valuetext={label}
    >
      <span style={{ width: `${percent}%` }} />
    </div>
  );
};

const DetailedGoalProgress = ({ goal }: { goal: SnapshotGoal }) => {
  const progress = goal.progress;
  if (progress.kind === 'recurring') {
    const consistencyLabel = `${progress.qualifying_periods} of ${progress.required_periods} ${plural(progress.required_periods, progress.period_unit)} complete`;
    return (
      <div className="snapshot-goal-details__progress">
        <p className="snapshot-goal-details__eyebrow">Progress detail</p>
        <div className="snapshot-goal-details__metrics">
          <div><span>This {progress.period_unit}</span><strong>{progress.current_period_value} of {progress.target_per_period} {progress.unit}</strong></div>
          <div><span>Consistency</span><strong>{progress.qualifying_periods} of {progress.required_periods} {plural(progress.required_periods, progress.period_unit)}</strong></div>
          <div><span>{progress.consecutive ? 'Current streak' : 'Successful periods'}</span><strong>{progress.consecutive ? progress.current_streak : progress.qualifying_periods} {plural(progress.consecutive ? progress.current_streak : progress.qualifying_periods, progress.period_unit)}</strong></div>
        </div>
        <GoalDetailMeter value={progress.qualifying_periods} target={progress.required_periods} label={consistencyLabel} />
        {progress.current_period_is_warm_up ? <p className="snapshot-goal-details__note">This is a getting-started period. Full measured periods begin {formatDate(progress.first_full_period_start)}.</p> : null}
      </div>
    );
  }

  if (progress.kind === 'count' || progress.kind === 'quantity') {
    const label = `${progress.value} of ${progress.target} ${progress.unit}`;
    return (
      <div className="snapshot-goal-details__progress">
        <p className="snapshot-goal-details__eyebrow">Progress detail</p>
        <div className="snapshot-goal-details__single-metric"><strong>{progress.value}</strong><span>of {progress.target} {progress.unit}</span></div>
        <GoalDetailMeter value={progress.value} target={progress.target} label={label} />
      </div>
    );
  }

  if (progress.kind === 'milestone' || progress.kind === 'deadline') {
    return (
      <div className="snapshot-goal-details__progress">
        <p className="snapshot-goal-details__eyebrow">{progress.kind === 'deadline' ? 'Steps to the deadline' : 'Milestones'}</p>
        {progress.kind === 'deadline' && progress.target_date ? <p className="snapshot-goal-details__deadline"><Clock3 aria-hidden="true" />Due {formatDate(progress.target_date)}</p> : null}
        <ul className="snapshot-goal-details__steps">
          {progress.steps.map((step) => (
            <li key={step.key} className={step.completed ? 'is-complete' : ''}>
              <span aria-hidden="true">{step.completed ? <Check /> : step.order + 1}</span>
              <span>{step.label}</span>
              <span className="sr-only">{step.completed ? 'Complete' : 'Not complete'}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (progress.kind === 'reflective') {
    return (
      <div className="snapshot-goal-details__progress">
        <p className="snapshot-goal-details__eyebrow">Reflection practice</p>
        <p className="snapshot-goal-details__prompt">{progress.prompt}</p>
        <div className="snapshot-goal-details__metrics">
          <div><span>Total reflections</span><strong>{progress.total_count}</strong></div>
          <div><span>This month</span><strong>{progress.month_count}</strong></div>
          <div><span>Finish line</span><strong>{progress.target ? `${progress.target} reflections` : 'Ongoing'}</strong></div>
        </div>
        {progress.target ? <GoalDetailMeter value={progress.total_count} target={progress.target} label={`${progress.total_count} of ${progress.target} reflections complete`} /> : null}
      </div>
    );
  }

  return <p className="snapshot-goal-details__note">Finish setting up tracking to see progress details here.</p>;
};

const GoalDetails = ({ goal, id }: { goal: SnapshotGoal; id: string }) => (
  <section id={id} className="snapshot-goal-details" aria-label={`Details for ${goal.title}`}>
    <div className="snapshot-goal-details__copy">
      <div>
        <p className="snapshot-goal-details__eyebrow">Why it matters</p>
        <p>{goal.motivation || 'No motivation has been added yet.'}</p>
      </div>
      <div>
        <p className="snapshot-goal-details__eyebrow">Next step</p>
        <p>{goal.next_action || 'No next step has been added yet.'}</p>
      </div>
    </div>
    <dl className="snapshot-goal-details__plan">
      <div><dt>Tracking plan</dt><dd>{goalTrackingPlan(goal)}</dd></div>
      <div><dt>Timing</dt><dd>{goal.start_date ? `Started ${formatDate(goal.start_date)}` : 'Start date not set'} · {goalCheckInCadence(goal.check_in_interval_days)}</dd></div>
    </dl>
    <DetailedGoalProgress goal={goal} />
  </section>
);

const GoalCardV2 = ({ goal, variant = 'due', onUpdate, onQuickUpdate, onQuickConfirmed, onEdit, onStatus, onBuildOn }: { goal: SnapshotGoal; variant?: 'due' | 'ledger'; onUpdate: (goal: SnapshotGoal, mode?: GoalProgressMode) => void; onQuickUpdate: (goal: SnapshotGoal, update: SnapshotProgressUpdate) => Promise<SnapshotGoal>; onQuickConfirmed: (confirmation: QuickGoalConfirmation) => void; onEdit: (goal: SnapshotGoal) => void; onStatus: (goal: SnapshotGoal, status: SnapshotGoalStatus) => void; onBuildOn?: (goal: SnapshotGoal) => void }) => {
  const [expanded, setExpanded] = useState(false);
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickCelebrating, setQuickCelebrating] = useState(false);
  const [quickError, setQuickError] = useState('');
  const [quickIdempotencyKey, setQuickIdempotencyKey] = useState(newIdempotencyKey);
  const quickCelebrationTimer = useRef<number | null>(null);
  const state = goalState(goal);
  const tallyProgress = goal.progress.kind === 'recurring' || goal.progress.kind === 'count' ? goal.progress : null;
  const completionLabel = tallyProgress?.completion_label || 'completion';
  const quickOccurredOn = goal.progress.kind === 'recurring' ? goal.progress.periods.current.occurred_on : localDate(goal.timezone);
  const primaryLabel = goal.status === 'paused'
    ? 'Resume goal'
    : !goal.can_accept_progress
      ? null
      : goal.progress.target_reached
        ? 'Mark complete'
        : tallyProgress
          ? 'Mark 1 ' + completionLabel + ' complete'
          : goal.progress.kind === 'reflective'
            ? 'Add reflection'
            : goal.progress.kind === 'milestone' || goal.progress.kind === 'deadline'
              ? 'Complete next step'
              : 'Update progress';
  const quickActionAccessibleLabel = quickCelebrating
    ? `${sentenceCase(completionLabel)} marked complete`
    : quickSaving
      ? `Marking one ${completionLabel} complete`
      : `Mark one ${completionLabel} complete`;
  const detailsId = `snapshot-goal-details-${goal.id}`;
  const titleDisclosure = (
    <button
      type="button"
      className="snapshot-goal-disclosure"
      aria-expanded={expanded}
      aria-controls={detailsId}
      aria-label={`${expanded ? 'Hide' : 'Show'} details for ${goal.title}`}
      onClick={() => setExpanded((open) => !open)}
    >
      <span>{goal.title}</span>
      <ChevronDown aria-hidden="true" />
    </button>
  );

  useEffect(() => () => {
    if (quickCelebrationTimer.current !== null) window.clearTimeout(quickCelebrationTimer.current);
  }, []);

  const quickLog = async () => {
    setQuickSaving(true);
    setQuickError('');
    try {
      const updated = await onQuickUpdate(goal, {
        idempotency_key: quickIdempotencyKey,
        occurred_on: quickOccurredOn,
        operation: 'increment',
        amount: 1,
      });
      const message = updated.progress.kind === 'recurring'
        ? `${sentenceCase(completionLabel)} marked complete — ${updated.progress.current_period_value} of ${updated.progress.target_per_period} this ${updated.progress.period_unit}.`
        : updated.progress.kind === 'count'
          ? `${sentenceCase(completionLabel)} marked complete — ${updated.progress.value} of ${updated.progress.target} total.`
          : `${sentenceCase(completionLabel)} marked complete.`;
      onQuickConfirmed({
        goal: updated,
        message,
        goalTargetReached: !goal.progress.target_reached && updated.progress.target_reached,
        weeklyTargetReached: reachedWeeklyTarget(goal, updated),
        undo: { idempotency_key: newIdempotencyKey(), occurred_on: quickOccurredOn, operation: 'decrement', amount: 1 },
      });
      setQuickCelebrating(true);
      if (quickCelebrationTimer.current !== null) window.clearTimeout(quickCelebrationTimer.current);
      const reducedMotion = typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      quickCelebrationTimer.current = window.setTimeout(() => {
        quickCelebrationTimer.current = null;
        setQuickCelebrating(false);
      }, reducedMotion ? 80 : 850);
      setQuickIdempotencyKey(newIdempotencyKey());
    } catch {
      setQuickError('We couldn’t confirm that update. Try again—the retry won’t count it twice.');
    } finally {
      setQuickSaving(false);
    }
  };

  const runPrimaryAction = () => {
    if (goal.status === 'paused') onStatus(goal, 'active');
    else if (goal.progress.target_reached) onStatus(goal, 'completed');
    else if (tallyProgress) void quickLog();
    else onUpdate(goal);
  };

  const primaryAction = primaryLabel ? (
    <Button
      type="button"
      variant={goal.status === 'paused' ? 'secondary' : 'default'}
      size="auto"
      className={quickCelebrating ? 'snapshot-goal-card__quick-confirmed' : ''}
      aria-label={tallyProgress && !goal.progress.target_reached ? quickActionAccessibleLabel : primaryLabel}
      disabled={quickSaving || quickCelebrating}
      onClick={runPrimaryAction}
    >
      {quickCelebrating && tallyProgress ? <><Check />{sentenceCase(completionLabel)} marked complete</> : quickSaving ? 'Marking…' : primaryLabel}
    </Button>
  ) : null;

  const ledgerOptionsMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid={'goal-options-' + goal.id} className="snapshot-goal-options" aria-label={'Options for ' + goal.title}><MoreHorizontal /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent data-testid={'goal-options-menu-' + goal.id} side="bottom" align="end" className="min-w-48 border border-foundation-calm-blue-100 bg-white">
        {goal.status === 'active' && goal.can_accept_progress && tallyProgress ? <DropdownMenuItem className="min-h-11" onSelect={() => onUpdate(goal, 'correct')}>Add note or adjust total</DropdownMenuItem> : null}
        <DropdownMenuItem className="min-h-11" onSelect={() => onEdit(goal)}>Edit setup</DropdownMenuItem>
        {goal.status === 'active' ? <DropdownMenuItem className="min-h-11" onSelect={() => onStatus(goal, 'paused')}>Pause</DropdownMenuItem> : null}
        {goal.status === 'paused' || goal.status === 'completed' ? <DropdownMenuItem className="min-h-11" onSelect={() => onStatus(goal, 'active')}>Resume</DropdownMenuItem> : null}
        {goal.status === 'completed' && onBuildOn ? <DropdownMenuItem className="min-h-11" disabled={!onBuildOn} onSelect={() => onBuildOn?.(goal)}>Build on this goal{!onBuildOn ? <LockKeyhole className="ml-auto h-4 w-4" aria-label="Locked" /> : null}</DropdownMenuItem> : null}
        {goal.status !== 'completed' ? <DropdownMenuItem className="min-h-11" onSelect={() => onStatus(goal, 'completed')}>Mark complete</DropdownMenuItem> : null}
        <DropdownMenuItem className="min-h-11 text-red-700 focus:text-red-700" onSelect={() => onStatus(goal, 'retired')}>Retire</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (variant === 'ledger') {
    return (
      <article data-testid="snapshot-ledger-goal-row" className={['snapshot-goal-ledger-row', expanded ? 'snapshot-goal-card--expanded' : '', quickCelebrating ? 'snapshot-goal-card--celebrating' : ''].filter(Boolean).join(' ')}>
        <span className="snapshot-goal-ledger-row__type">{goal.goal_type || 'Goal'}</span>
        <div className="snapshot-goal-ledger-row__title">
          <h4>{titleDisclosure}</h4>
          {state.label !== 'Active' ? <span className={'snapshot-status ' + state.className}><span aria-hidden="true">{state.glyph}</span>{state.label}</span> : null}
        </div>
        <p className="snapshot-goal-ledger-row__summary" aria-label={compactGoalSummaryLabel(goal)}>{compactGoalSummary(goal)}</p>
        <div className="snapshot-goal-ledger-row__action">{primaryAction}</div>
        {ledgerOptionsMenu}
        {expanded ? <GoalDetails goal={goal} id={detailsId} /> : null}
        {goal.source_changed ? <p className="snapshot-goal-card__source-change"><RefreshCw />The Life Plan answer behind this goal changed. Review when you like.</p> : null}
        {quickError ? <p role="alert" className="snapshot-goal-ledger-row__feedback snapshot-goal-card__error">{quickError}</p> : null}
      </article>
    );
  }

  return (
    <article data-testid="snapshot-ready-goal-row" className={`snapshot-card snapshot-goal-card snapshot-goal-card-v2 snapshot-goal-card-v2--${variant} ${expanded ? 'snapshot-goal-card--expanded' : ''} ${goal.status === 'completed' ? 'snapshot-goal-card--completed' : ''} ${quickCelebrating ? 'snapshot-goal-card--celebrating' : ''}`}>
      <h4 className="snapshot-goal-card__title">{titleDisclosure}</h4>
      <span className="sr-only">{state.label}; check in today</span>
      {goal.source_changed ? <p className="snapshot-goal-card__source-change"><RefreshCw />The Life Plan answer behind this goal changed. Your tracking and progress are untouched—review when you like.</p> : null}
      <p className="snapshot-goal-card__summary" aria-label={compactGoalSummaryLabel(goal)}>{compactGoalSummary(goal)}</p>
      <div className="snapshot-goal-card__progress"><CompactGoalProgress goal={goal} celebrating={quickCelebrating} /></div>
      <div className="snapshot-goal-card__actions">{primaryAction}</div>
      <div className="snapshot-goal-card__topline">{ledgerOptionsMenu}</div>
      {expanded ? <GoalDetails goal={goal} id={detailsId} /> : null}

      {quickError ? <p role="alert" className="snapshot-goal-card__error">{quickError}</p> : null}
    </article>
  );
};

const GoalSourceCard = ({
  source,
  index,
  suggesting,
  suggestionError,
  onSuggest,
  onManual,
}: {
  source: SnapshotContentItem;
  index: number;
  suggesting: boolean;
  suggestionError: boolean;
  onSuggest: () => void;
  onManual: () => void;
}) => {
  if (!snapshotItemUnlocked(source)) return null;

  const entries = labeledValue(source);
  const isHabit = source.reflection_format === 'habit_v1';
  const titleEntry = isHabit ? entries.find((entry) => entry.label === 'If-then') : entries.find((entry) => entry.value);
  const title = titleEntry?.value;
  const details = isHabit ? entries : entries.slice(1);

  return (
    <article className="snapshot-goal-idea">
      <div className="snapshot-goal-idea__copy">
        <span className="snapshot-eyebrow text-muted-foreground">Life Plan {isHabit ? 'habit' : 'goal'} {index + 1}{source.is_draft ? ' · Draft' : ''}</span>
        <strong>{title || 'A goal waiting to be shaped'}</strong>
        {source.is_answered ? (
          <details>
            <summary>View reflection details</summary>
            <div className="snapshot-goal-idea__details">
              {details.map((entry) => entry.value ? <div key={entry.label}><span>{entry.label}</span><p>{entry.value}</p></div> : null)}
            </div>
          </details>
        ) : <p>Capture the goal, timing, cue, reward, and first step in your Life Plan.</p>}
        {suggestionError ? <p role="alert" className="snapshot-goal-card__error">We couldn’t build a suggestion. Try again or set it up manually.</p> : null}
      </div>
      <div className="snapshot-goal-idea__actions">
        {source.is_answered ? (
          <>
            <Button type="button" size="auto" className="min-h-11" aria-label="Suggest a way to track this" disabled={suggesting} onClick={onSuggest}>{suggesting ? 'Building…' : 'Suggest a way to track this'}</Button>
            <Button type="button" variant="ghost" size="auto" className="min-h-11" onClick={onManual}>Set up manually</Button>
          </>
        ) : null}
      </div>
    </article>
  );
};

const CompletedGoalRow = ({
  goal,
  onStatus,
  onBuildOn,
}: {
  goal: SnapshotGoal;
  onStatus: (goal: SnapshotGoal, status: SnapshotGoalStatus) => void;
  onBuildOn?: (goal: SnapshotGoal) => void;
}) => (
  <article className="snapshot-completed-goal">
    <span className="snapshot-completed-goal__check" aria-hidden="true"><Check /></span>
    <div>
      <strong>{goal.title}</strong>
      <p>{goalSummary(goal)}{goal.last_updated_at ? ` · completed ${formatDate(goal.last_updated_at)}` : ''}</p>
    </div>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid={`goal-options-${goal.id}`} className="snapshot-goal-options ml-auto shrink-0" aria-label={`Options for ${goal.title}`}><MoreHorizontal /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent data-testid={`goal-options-menu-${goal.id}`} side="bottom" align="end" className="min-w-48 border border-foundation-calm-blue-100 bg-white">
        <DropdownMenuItem className="min-h-11" onSelect={() => onStatus(goal, 'active')}>Resume</DropdownMenuItem>
        <DropdownMenuItem className="min-h-11" disabled={!onBuildOn} onSelect={() => onBuildOn?.(goal)}>Build on this goal{!onBuildOn ? <LockKeyhole className="ml-auto h-4 w-4" aria-label="Locked" /> : null}</DropdownMenuItem>
        <DropdownMenuItem className="min-h-11 text-red-700 focus:text-red-700" onSelect={() => onStatus(goal, 'retired')}>Retire</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </article>
);

export const Snapshot = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<SnapshotPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [editingContent, setEditingContent] = useState<SnapshotContentItem | null>(null);
  const [progressGoal, setProgressGoal] = useState<SnapshotGoal | null>(null);
  const [progressMode, setProgressMode] = useState<GoalProgressMode>('quick');
  const [progressInitialConfirmation, setProgressInitialConfirmation] = useState<GoalProgressConfirmation | null>(null);
  const [quickConfirmation, setQuickConfirmation] = useState<QuickGoalConfirmation | null>(null);
  const [quickConfirmationUndoing, setQuickConfirmationUndoing] = useState(false);
  const [quickConfirmationError, setQuickConfirmationError] = useState('');
  const [goalEditor, setGoalEditor] = useState<{ goal: SnapshotGoal | null; sourceKey?: string; input?: SnapshotGoalInput | null } | null>(null);
  const [suggesting, setSuggesting] = useState<string | null>(null);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [lifecycle, setLifecycle] = useState<{ goal: SnapshotGoal; status: SnapshotGoalStatus } | null>(null);
  const [completedGoalPrompt, setCompletedGoalPrompt] = useState<SnapshotGoal | null>(null);
  const [activeSection, setActiveSection] = useState('snapshot-where-you-are');
  const [thoughtLoopOpen, setThoughtLoopOpen] = useState(false);
  const [challengeOpen, setChallengeOpen] = useState<number | null>(null);
  const [visionOpen, setVisionOpen] = useState(true);
  const [missionOpen, setMissionOpen] = useState(false);
  const [flourishingOpen, setFlourishingOpen] = useState<number | null>(0);
  const [supportOpen, setSupportOpen] = useState<number | null>(0);
  const externalGoalUpdates = useRef(new Map<string, SnapshotGoal>());
  const activeSnapshotLoads = useRef(0);
  const initialSectionChosen = useRef(false);
  const quickConfirmationTimer = useRef<number | null>(null);

  const load = useCallback(async () => {
    activeSnapshotLoads.current += 1;
    setLoading(true); setError(false);
    try {
      const payload = await snapshot.get();
      const merged = Array.from(externalGoalUpdates.current.values()).reduce(mergeGoalIntoSnapshot, payload);
      setData(merged);
      if (!initialSectionChosen.current) {
        setActiveSection(merged.due_goal_ids.length ? 'snapshot-how-you-get-there' : 'snapshot-where-you-are');
        initialSectionChosen.current = true;
      }
    } catch { setError(true); } finally {
      activeSnapshotLoads.current -= 1;
      if (activeSnapshotLoads.current === 0) externalGoalUpdates.current.clear();
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => subscribeToSnapshotGoalUpdates((goal) => {
    if (activeSnapshotLoads.current > 0) externalGoalUpdates.current.set(goal.id, goal);
    setData((current) => current ? mergeGoalIntoSnapshot(current, goal) : current);
  }), []);
  useEffect(() => () => {
    if (quickConfirmationTimer.current !== null) window.clearTimeout(quickConfirmationTimer.current);
  }, []);

  useEffect(() => {
    const goalId = searchParams.get('editGoal');
    const buildOnId = searchParams.get('buildOn');
    const createNew = searchParams.get('newGoal') === '1';
    if (!data || (!goalId && !buildOnId && !createNew)) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('editGoal');
    nextParams.delete('buildOn');
    nextParams.delete('newGoal');
    setSearchParams(nextParams, { replace: true });

    if ((buildOnId || createNew) && !snapshotGoalsUnlocked(data.content)) setActiveSection('snapshot-how-you-get-there');

    const selectedGoal = goalId ? data.goals.find((goal) => goal.id === goalId) : null;
    const sourceGoal = buildOnId ? data.goals.find((goal) => goal.id === buildOnId) : null;
    if (selectedGoal) {
      setGoalEditor({
        goal: selectedGoal,
        sourceKey: selectedGoal.source_question_key || undefined,
      });
    } else if (sourceGoal && snapshotGoalsUnlocked(data.content)) {
      setGoalEditor({ goal: null, input: successorInput(sourceGoal) });
    } else if (createNew && snapshotGoalsUnlocked(data.content)) {
      setGoalEditor({ goal: null });
    }
  }, [data, searchParams, setSearchParams]);

  const content = data?.content || {};
  const canCreateGoal = snapshotGoalsUnlocked(content);
  const goalsBySource = useMemo(() => new Map((data?.goals || []).filter((goal) => goal.source_question_key).map((goal) => [goal.source_question_key, goal])), [data?.goals]);
  const saveGoal = async () => { setGoalEditor(null); await load(); };
  const applyProgressResult = (goal: SnapshotGoal) => setData((current) => current ? mergeGoalIntoSnapshot(current, goal) : current);
  const saveProgress = (goal: SnapshotGoal) => {
    setProgressGoal(goal);
    applyProgressResult(goal);
    if (goal.status === 'completed') setCompletedGoalPrompt(goal);
  };
  const openProgress = (goal: SnapshotGoal, mode: GoalProgressMode = 'quick') => {
    setProgressInitialConfirmation(null);
    setProgressMode(mode);
    setProgressGoal(goal);
  };
  const closeProgress = () => {
    setProgressGoal(null);
    setProgressMode('quick');
    setProgressInitialConfirmation(null);
  };
  const quickUpdate = async (goal: SnapshotGoal, update: SnapshotProgressUpdate) => {
    const refreshed = await snapshot.updateGoalProgress(goal.id, update);
    applyProgressResult(refreshed);
    return refreshed;
  };
  const dismissQuickConfirmation = () => {
    if (quickConfirmationTimer.current !== null) window.clearTimeout(quickConfirmationTimer.current);
    quickConfirmationTimer.current = null;
    setQuickConfirmation(null);
    setQuickConfirmationError('');
  };
  const presentQuickConfirmation = (confirmation: QuickGoalConfirmation) => {
    dismissQuickConfirmation();
    if (confirmation.goalTargetReached) {
      setProgressInitialConfirmation(null);
      setProgressMode('quick');
      setProgressGoal(confirmation.goal);
      return;
    }
    if (confirmation.weeklyTargetReached) {
      setProgressInitialConfirmation(confirmation);
      setProgressMode('quick');
      setProgressGoal(confirmation.goal);
      return;
    }
    setQuickConfirmation(confirmation);
    quickConfirmationTimer.current = window.setTimeout(() => {
      quickConfirmationTimer.current = null;
      setQuickConfirmation(null);
    }, 5000);
  };
  const undoQuickConfirmation = async () => {
    if (!quickConfirmation?.undo) return;
    if (quickConfirmationTimer.current !== null) window.clearTimeout(quickConfirmationTimer.current);
    quickConfirmationTimer.current = null;
    setQuickConfirmationUndoing(true);
    setQuickConfirmationError('');
    try {
      const refreshed = await snapshot.updateGoalProgress(quickConfirmation.goal.id, quickConfirmation.undo);
      applyProgressResult(refreshed);
      setQuickConfirmation(null);
    } catch {
      setQuickConfirmationError('We couldn’t undo that update. Please try again.');
    } finally {
      setQuickConfirmationUndoing(false);
    }
  };
  const saveContent = (item: SnapshotContentItem) => setData((current) => current ? { ...current, content: { ...current.content, [item.key]: item } } : current);

  const suggest = async (sourceKey: string) => {
    setSuggesting(sourceKey); setSuggestionError(null);
    try { const result = await snapshot.suggestGoal(sourceKey); setGoalEditor({ goal: null, sourceKey, input: result.proposal }); }
    catch { setSuggestionError(sourceKey); }
    finally { setSuggesting(null); }
  };

  if (loading) return <main className="snapshot-page" aria-label="Loading your Snapshot">
    <SnapshotPageHeader updatedLine="Loading your Snapshot…" />
    <section className="snapshot-loading" aria-busy="true" aria-live="polite">
      <span>Loading your Snapshot…</span>
      <div className="snapshot-collage-skeleton" data-testid="snapshot-collage-skeleton">
        {['pillars','motivation','direction','values','goals','resets','partner'].map((item) => <div key={item} data-testid="snapshot-collage-skeleton-cell" className={`snapshot-skeleton snapshot-skeleton--${item}`} />)}
      </div>
    </section>
  </main>;
  if (error || !data) return <main className="snapshot-page">
    <SnapshotPageHeader updatedLine="Your saved work is still safe." />
    <div role="alert" className="snapshot-load-error"><Icon name="caution" size={30} aria-hidden="true" /><h2>We couldn’t load your Snapshot</h2><p>Nothing has been lost. Your Life Plan and goals are safe—this view just failed to load.</p><span><Button type="button" size="auto" onClick={load}>Try again</Button><Button asChild variant="secondary" size="auto"><Link to="/training">Review Life Plan</Link></Button></span></div>
  </main>;

  const motivation = content.snapshot_motivation;
  const mindset = content.mindset_oneliner;
  const vision = content.cb_vision_draft;
  const mission = content.cb_mission_draft;
  const resets = content.lp_0350;
  const accountability = content.snapshot_acocuntabilitypartner;
  const accountDisplay = accountability?.display_value as { name?: string; detail?: string } | undefined;
  const hasLifePlanContent = Object.values(data.content).some(snapshotItemUnlocked);
  const activeGoals = data.goals.filter((goal) => goal.status === 'active');
  const currentGoals = data.goals.filter((goal) => goal.status === 'active' || goal.status === 'paused');
  const dueGoals = activeGoals.filter((goal) => goal.is_due);
  const otherGoals = currentGoals.filter((goal) => !goal.is_due);
  const completedGoals = data.goals.filter((goal) => goal.status === 'completed');
  const untrackedGoalSources = (['snapshot_habit1','snapshot_habit2','snapshot_habit3'] as const)
    .filter((sourceKey) => snapshotItemUnlocked(content[sourceKey]) && !goalsBySource.has(sourceKey));
  const ratedAxes = data.radar?.axes.filter((axis) => axis.latest_value !== null) || [];
  const hasRatedAxes = ratedAxes.length > 0;
  const strongestAxis = ratedAxes.reduce<typeof ratedAxes[number] | null>((best, axis) => !best || Number(axis.latest_value) > Number(best.latest_value) ? axis : best, null);
  const growthAxis = ratedAxes.reduce<typeof ratedAxes[number] | null>((lowest, axis) => !lowest || Number(axis.latest_value) < Number(lowest.latest_value) ? axis : lowest, null);
  const ratedLine = data.radar ? `${ratedAxes.length} of ${data.radar.axes.length} rated` : 'No ratings yet';
  const thoughtEntries = labeledValue(content.lp_0329);
  const thought = thoughtEntries.find((entry) => entry.label === 'Thought')?.value;
  const truth = thoughtEntries.find((entry) => entry.label === 'Wiser truth')?.value;
  const flourishing = labeledValue(content.snapshot_flourishing);
  const values = listValue(content.snapshot_valuesalways);
  const resetValues = listValue(resets);
  const partnerName = accountDisplay?.name || 'Your person';
  const partnerInitials = partnerName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
  const stopValues = listValue(content.snapshot_stop);
  const supportItems = [
    { id: 'snapshot-routine', item: content.daily_routine, title: 'Ideal daily rhythm', hint: content.daily_routine.is_answered ? 'A rhythm you can return to' : 'Nothing named yet', body: textValue(content.daily_routine), empty: 'Sketch a morning, daytime, and evening you could realistically hold.' },
    { id: 'snapshot-stop', item: content.snapshot_stop, title: 'What to stop', hint: stopValues.length ? `${stopValues.length} ${plural(stopValues.length, 'thing')} to put down` : 'Nothing named yet', list: stopValues, empty: 'Name the actions you are choosing to stop so there is room for what matters.' },
    { id: 'snapshot-resets', item: resets, title: 'Restorative activities', hint: resetValues.length ? `${resetValues.length} resets named` : 'Nothing named yet', list: resetValues, empty: 'Add the activities that restore and steady you.' },
    { id: 'snapshot-accountability', item: accountability, title: 'Accountability partner', hint: accountability.is_answered ? partnerName : 'No one named yet', body: accountDisplay?.detail || textValue(accountability), empty: 'Name who is in your corner and what you want them to say if you drift.' },
  ];

  const navigateToSection = (id: string) => {
    setActiveSection(id);
    window.requestAnimationFrame(() => scrollTo('snapshot-section-tabs'));
  };

  return <main className="snapshot-page">
    <SnapshotPageHeader season={data.season.label} progress={data.content_progress} updatedLine={hasLifePlanContent ? <>Updated {formatDate(data.last_updated_at)} · drawn from your Life Plan</> : 'Complete Life Plan reflections to build your Snapshot'} />

    {dueGoals.length ? <section className="snapshot-attention" role="status"><span className="snapshot-attention__icon" aria-hidden="true"><Clock3 /></span><div><strong>{dueGoals.length} {plural(dueGoals.length, 'goal')} {dueGoals.length === 1 ? 'is' : 'are'} ready for today’s check-in</strong><p>A quick update keeps your Snapshot current.</p></div><Button type="button" size="auto" onClick={requestSnapshotGoalCheckIn}>Check in now</Button></section> : null}

    <section aria-labelledby="snapshot-glance-title" className="snapshot-collage">
      <h2 id="snapshot-glance-title">Your season at a glance</h2>
      <div className="snapshot-collage__grid">
        {snapshotItemUnlocked(mindset) && snapshotItemUnlocked(vision) ? (<button type="button" className="snapshot-collage-card snapshot-collage-card--direction" onClick={() => navigateToSection('snapshot-where-you-want-to-go')}>
          <Icon name="mountain" size={104} aria-hidden="true" />
          <span className="snapshot-collage-card__label">Your direction</span>
          <p>{mindset.is_answered ? textValue(mindset) : 'Write a line that remembers the attitude you want to bring.'}</p>
          <small>{vision.is_answered ? textValue(vision) : 'Your vision will appear here once you capture it.'}</small>
          <span className="snapshot-collage-card__footer">Vision, mission & flourishing →</span>
        </button>) : !snapshotItemUnlocked(mindset) && !snapshotItemUnlocked(vision) ? <SnapshotLock title="Your direction" className="snapshot-collage-card snapshot-collage-card--direction" /> : (<article className="snapshot-collage-card snapshot-collage-card--direction snapshot-direction-partial">
          <span className="snapshot-collage-card__label">Your direction</span>
          {snapshotItemUnlocked(mindset) ? <p>{textValue(mindset)}</p> : <Link className="snapshot-direction-partial__lock" to={LIFE_PLAN_COURSE_PATH} aria-label="Mindset — Open Life Plan"><LockKeyhole aria-hidden="true" /><span>Mindset</span></Link>}
          {snapshotItemUnlocked(vision) ? <p>{textValue(vision)}</p> : <Link className="snapshot-direction-partial__lock" to={LIFE_PLAN_COURSE_PATH} aria-label="Vision — Open Life Plan"><LockKeyhole aria-hidden="true" /><span>Vision</span></Link>}
          <button type="button" className="snapshot-collage-card__footer" onClick={() => navigateToSection('snapshot-where-you-want-to-go')}>Vision, mission &amp; flourishing →</button>
        </article>)}

        <SnapshotContentGate item={motivation} title="What’s motivating this season" className="snapshot-collage-card snapshot-collage-card--motivation"><button type="button" className="snapshot-collage-card snapshot-collage-card--motivation" onClick={() => navigateToSection('snapshot-where-you-are')}>
          <Icon name="flame" size={104} aria-hidden="true" />
          <span className="snapshot-collage-card__label">What’s motivating this season</span>
          <p>{motivation.is_answered ? textValue(motivation) : 'Name the fire underneath this season—what is calling you toward change.'}</p>
          <span className="snapshot-collage-card__footer">In your words · {formatDate(motivation.updated_at)} · Where you are →</span>
        </button></SnapshotContentGate>

        <button type="button" aria-label={`Ten Pillars overview — Where you are. ${ratedLine}`} className="snapshot-collage-card snapshot-collage-card--pillars" onClick={() => navigateToSection('snapshot-where-you-are')}>
          <span className="snapshot-collage-card__label">Ten Pillars</span>
          <div className="snapshot-collage-card__radar"><MiniRadar data={data.radar} />{hasRatedAxes ? <div className="snapshot-collage-signals"><span><small>Strongest right now</small><strong>{strongestAxis?.label} · {strongestAxis?.latest_value}/{data.radar?.definition.scale_max}</strong></span><span><small>Room to grow</small><strong>{growthAxis?.label} · {growthAxis?.latest_value}/{data.radar?.definition.scale_max}</strong></span></div> : null}</div>
          <span className="snapshot-collage-card__footer">{ratedLine} · View full chart →</span>
        </button>

        <SnapshotContentGate item={content.snapshot_valuesalways} title="Values you already live" className="snapshot-collage-card snapshot-collage-card--values"><button type="button" className="snapshot-collage-card snapshot-collage-card--values" onClick={() => navigateToSection('snapshot-where-you-are')}>
          <span className="snapshot-collage-card__label">Values you already live</span>
          {values.length ? <span className="snapshot-collage-values">{values.slice(0, 3).map((value) => <span key={value} title={value}>{value}</span>)}{values.length > 3 ? <em>+{values.length - 3} more</em> : null}</span> : <p className="snapshot-collage-empty">Name the values that already show up in how you live.</p>}
          <span className="snapshot-collage-card__footer">Where you are →</span>
        </button></SnapshotContentGate>

        {!canCreateGoal && !currentGoals.length ? <SnapshotLock title="Goals in motion" className="snapshot-collage-card snapshot-collage-card--goals" /> : (<button type="button" className="snapshot-collage-card snapshot-collage-card--goals" onClick={() => navigateToSection('snapshot-how-you-get-there')}>
          <span className="snapshot-collage-card__top"><span className="snapshot-collage-card__label">Goals in motion</span><small>{activeGoals.length} active · {dueGoals.length} due today</small></span>
          {currentGoals.length ? <span className="snapshot-collage-goals">{currentGoals.slice(0, 3).map((goal) => { const state = goalState(goal); return <span key={goal.id}><span><strong>{goal.title}</strong><small>{goalSummary(goal)}</small></span><em className={state.className}><i aria-hidden="true">{state.glyph}</i>{goal.is_due ? 'Today' : state.label}</em></span>; })}{currentGoals.length > 3 ? <b>+{currentGoals.length - 3} more goals</b> : null}</span> : <span className="snapshot-collage-goals-empty"><strong>Nothing tracked yet</strong><p>Your Life Plan already names a few things you want to change. Turning one into a goal is how it starts showing up here.</p><span>Add a goal</span></span>}
          <span className="snapshot-collage-card__footer">Open the goal workspace →</span>
        </button>)}

        <SnapshotContentGate item={resets} title="Your resets" className="snapshot-collage-card snapshot-collage-card--resets"><button type="button" className="snapshot-collage-card snapshot-collage-card--resets" onClick={() => navigateToSection('snapshot-how-you-get-there')}>
          <span className="snapshot-collage-card__label">Your resets</span>
          {resetValues.length ? <span className="snapshot-collage-resets">{resetValues.slice(0, 3).map((value, index) => <span key={value} title={value}><i>0{index + 1}</i><span>{value}</span></span>)}{resetValues.length > 3 ? <b>+{resetValues.length - 3} more</b> : null}</span> : <p className="snapshot-collage-empty">Name what restores you and it will live here.</p>}
          <span className="snapshot-collage-card__footer">How you get there →</span>
        </button></SnapshotContentGate>

        <SnapshotContentGate item={accountability} title="Who’s in your corner" className="snapshot-collage-card snapshot-collage-card--partner"><button type="button" className="snapshot-collage-card snapshot-collage-card--partner" onClick={() => navigateToSection('snapshot-how-you-get-there')}>
          <span className="snapshot-collage-card__label">In your corner</span>
          {accountability.is_answered ? <span className="snapshot-collage-partner"><span><i aria-hidden="true">{partnerInitials}</i><strong>{partnerName}</strong></span><p>{accountDisplay?.detail || textValue(accountability)}</p></span> : <p className="snapshot-collage-empty">No one named yet. One person who checks in makes a difference.</p>}
          <span className="snapshot-collage-card__footer">How you get there →</span>
        </button></SnapshotContentGate>
      </div>
    </section>

    <nav id="snapshot-section-tabs" className="snapshot-section-nav" aria-label="Snapshot sections">
      <span className="snapshot-section-nav__mobile-label" aria-hidden="true">Jump to</span>
      <div className="snapshot-section-nav__items" data-testid="snapshot-section-switcher-items">{[
        ['snapshot-where-you-are','01','Where you are','where_you_are'],
        ['snapshot-where-you-want-to-go','02','Where you want to go','where_you_want_to_go'],
        ['snapshot-how-you-get-there','03','How you get there','how_you_get_there'],
      ].map(([id, number, label, section]) => {
        const items = Object.values(content).filter((item) => item.section === section);
        const answered = items.filter((item) => item.is_answered).length;
        return <button key={id} type="button" aria-describedby={`${id}-progress`} aria-current={activeSection === id ? 'page' : undefined} onClick={() => navigateToSection(id)}><span className="snapshot-section-nav__number">{number}</span><span className="snapshot-section-nav__copy"><span className="snapshot-section-nav__label">{label}</span><span id={`${id}-progress`} className="snapshot-section-nav__progress" aria-hidden="true">{answered} of {items.length} reflections answered</span></span>{id === 'snapshot-how-you-get-there' && dueGoals.length ? <span className="snapshot-section-nav__badge">{dueGoals.length} due</span> : null}</button>;
      })}</div>
    </nav>

    <section id="snapshot-where-you-are" className="snapshot-section" aria-labelledby="snapshot-where-you-are-title" hidden={activeSection !== 'snapshot-where-you-are'}>
      <SectionHeader headingId="snapshot-where-you-are-title" number="01" title="Where you are" action={<SectionEditMenu label="Edit Where you are" items={[motivation, content.snapshot_valuesalways, content.snapshot_gaparea, content.snapshot_challenges, content.lp_0329]} onEdit={setEditingContent} />} />
      <SnapshotRadar radar={data.radar} />

      <div className="snapshot-now-grid">
        <SnapshotContentGate item={motivation} title="What’s motivating this season" className="snapshot-motivation-card" id="snapshot-motivation"><article id="snapshot-motivation" className="snapshot-motivation-card">
          <Icon name="flame" size={96} aria-hidden="true" />
          <span className="snapshot-eyebrow">What’s motivating this season</span>
          {motivation.is_answered ? <p>{textValue(motivation)}</p> : <p className="snapshot-empty-copy">Name the fire underneath this season—pain, growth, a person, or something you are ready to change.</p>}
          {motivation.updated_at ? <small>In your words · {formatDate(motivation.updated_at)}</small> : null}
        </article></SnapshotContentGate>
        <article className="snapshot-identity-card">
          <SnapshotContentGate item={content.snapshot_valuesalways} title="Values you already live" className="snapshot-values"><div className="snapshot-values"><h3>Values you already live</h3>{content.snapshot_valuesalways.is_answered ? <div className="snapshot-value-pills">{values.map((value) => <span key={value}>{value}</span>)}</div> : <p className="snapshot-empty-copy">Name the values that already show up in how you live.</p>}</div></SnapshotContentGate>
          <SnapshotContentGate item={content.snapshot_gaparea} title="Areas you’re excited to grow in" className="snapshot-growth"><div className="snapshot-growth"><h3>Areas you’re excited to grow in</h3>{content.snapshot_gaparea.is_answered ? <p>{textValue(content.snapshot_gaparea)}</p> : <p className="snapshot-empty-copy">Not answered yet in your Life Plan.</p>}</div></SnapshotContentGate>
        </article>
      </div>

      <SnapshotContentGate item={content.snapshot_challenges} title="Challenges & your response plans" className="snapshot-challenges">
        <article className="snapshot-challenges">
          <div className="snapshot-block-heading"><h3>Challenges & your response plans</h3><p>Tap a challenge to see or edit your plan</p></div>
          {content.snapshot_challenges.is_answered ? <div className="snapshot-challenge-list">
            {(content.snapshot_challenges.display_value as { challenge: string; plan: string; raw: string }[]).map((entry, index) => {
              const open = challengeOpen === index;
              return <article key={`${entry.challenge}-${index}`} className={open ? 'is-open' : ''}>
                <button type="button" aria-expanded={open} onClick={() => setChallengeOpen(open ? null : index)}><span>0{index + 1}</span><strong>{entry.challenge}</strong><ChevronDown aria-hidden="true" /></button>
                {open ? <div>
                  <b>Your response plan</b>
                  <p>{entry.plan || 'No response plan added yet.'}</p>
                  <Button type="button" variant="secondary" size="auto" className="min-h-11 self-start" onClick={() => setEditingContent(content.snapshot_challenges)}>{entry.plan ? 'Edit response plan' : 'Add response plan'}</Button>
                </div> : null}
              </article>;
            })}
          </div> : <Empty>Name the challenges in front of you and how you are seeking to meet them.</Empty>}
        </article>
      </SnapshotContentGate>

      <SnapshotContentGate item={content.lp_0329} title="Recurring thought" className="snapshot-thought-card">{content.lp_0329.is_answered ? <article className="snapshot-thought-card"><h3>Recurring thought</h3><p className="snapshot-thought-card__thought">{thought}</p><button type="button" className="snapshot-thought-toggle" aria-expanded={thoughtLoopOpen} aria-controls="snapshot-wiser-truth" onClick={() => setThoughtLoopOpen((open) => !open)}>{thoughtLoopOpen ? 'Hide the wiser truth' : 'See the wiser truth'}<ChevronDown aria-hidden="true" /></button>{thoughtLoopOpen ? <div id="snapshot-wiser-truth" className="snapshot-wiser-truth"><small>The wiser truth</small><p>{truth}</p></div> : null}</article> : null}</SnapshotContentGate>
    </section>

    <section id="snapshot-where-you-want-to-go" className="snapshot-section" aria-labelledby="snapshot-where-you-want-to-go-title" hidden={activeSection !== 'snapshot-where-you-want-to-go'}>
      <SectionHeader headingId="snapshot-where-you-want-to-go-title" number="02" title="Where you want to go" action={<SectionEditMenu label="Edit Where you want to go" items={[mindset, vision, mission, content.success_refined, content.snapshot_flourishing]} onEdit={setEditingContent} />} />
      <SnapshotContentGate item={mindset} title="Your mindset for this season" className="snapshot-mindset-card"><article className="snapshot-mindset-card"><Icon name="mountain" size={210} aria-hidden="true" /><span className="snapshot-eyebrow">Your mindset for this season</span>{mindset.is_answered ? <p>{textValue(mindset)}</p> : <p className="snapshot-empty-copy">Write yourself a one-liner—the attitude you want to bring this season.</p>}</article></SnapshotContentGate>

      <div className="snapshot-vision-mission">
        <SnapshotContentGate item={vision} title="Vision"><article><h3>Vision</h3>{vision.is_answered ? <ExpandableText text={textValue(vision)} expanded={visionOpen} onToggle={() => setVisionOpen((open) => !open)} label="Read more" className="snapshot-vision-text" /> : <p className="snapshot-empty-copy">Your vision will appear here once you capture it.</p>}</article></SnapshotContentGate>
        <SnapshotContentGate item={mission} title="Mission"><article><h3>Mission</h3>{mission.is_answered ? <ExpandableText text={textValue(mission)} expanded={missionOpen} onToggle={() => setMissionOpen((open) => !open)} label="Read more" className="snapshot-mission-text whitespace-pre-wrap" /> : <p className="snapshot-empty-copy">You haven’t written a mission yet. It’s the one line that says who this is all for.</p>}</article></SnapshotContentGate>
      </div>

      <SnapshotContentGate item={content.success_refined} title="How you’ll know it’s working" className="snapshot-success-card"><article className="snapshot-success-card"><span className="snapshot-eyebrow">How you’ll know it’s working</span>{content.success_refined.is_answered ? <p>{textValue(content.success_refined)}</p> : <p className="snapshot-empty-copy">Define success around what is in your control.</p>}</article></SnapshotContentGate>

      <SnapshotContentGate item={content.snapshot_flourishing} title="Flourishing across seven categories" className="snapshot-flourishing"><article className="snapshot-flourishing"><div className="snapshot-block-heading"><h3>Flourishing across seven categories</h3><p>One at a time</p></div>{content.snapshot_flourishing.is_answered ? <div className="snapshot-flourishing__list">{flourishing.map((entry, index) => { const open = flourishingOpen === index; return <div key={entry.label} className={open ? 'is-open' : ''}><button type="button" aria-expanded={open} onClick={() => setFlourishingOpen(open ? null : index)}><i aria-hidden="true" /><strong>{entry.label}</strong>{!open ? <span>{entry.value}</span> : null}<ChevronDown aria-hidden="true" /></button>{open ? <p>{entry.value}</p> : null}</div>; })}</div> : <Empty>Describe what thriving looks like across seven parts of your life.</Empty>}</article></SnapshotContentGate>
    </section>

    <section id="snapshot-how-you-get-there" className="snapshot-section" aria-labelledby="snapshot-how-you-get-there-title" hidden={activeSection !== 'snapshot-how-you-get-there'}>
      <SectionHeader headingId="snapshot-how-you-get-there-title" number="03" title="How you get there" action={<Button type="button" variant="secondary" size="auto" className="snapshot-section-add" disabled={!canCreateGoal} onClick={() => setGoalEditor({ goal: null })}>{!canCreateGoal ? <LockKeyhole aria-hidden="true" /> : null}Add a goal</Button>} />

      <div id="snapshot-goals" className="snapshot-goals-workspace">
        {!canCreateGoal ? <SnapshotLock title="Add a goal" /> : null}
        {completedGoalPrompt && canCreateGoal ? (
          <aside className="snapshot-goal-complete-prompt" aria-label="Choose what comes after your completed goal">
            <div className="snapshot-goal-complete-prompt__copy" data-testid="completed-goal-prompt-copy"><p className="font-semibold text-green-950">Goal completed</p><p className="mt-1 text-sm text-green-900">“{completedGoalPrompt.title}” is safely in your history. Ready to choose what comes next?</p></div>
            <div className="snapshot-goal-complete-prompt__actions" data-testid="completed-goal-prompt-actions">
              <Button type="button" size="auto" className="snapshot-goal-complete-prompt__primary" onClick={() => { setCompletedGoalPrompt(null); setGoalEditor({ goal: null }); }}>Set a new goal</Button>
              <div className="snapshot-goal-complete-prompt__secondary-actions" data-testid="completed-goal-prompt-secondary-actions">
                <Button type="button" variant="secondary" size="auto" onClick={() => { const completed = completedGoalPrompt; setCompletedGoalPrompt(null); setGoalEditor({ goal: null, input: successorInput(completed) }); }}>Build on this goal</Button>
                <Button type="button" variant="ghost" size="auto" onClick={() => setCompletedGoalPrompt(null)}>Not now</Button>
              </div>
            </div>
          </aside>
        ) : null}

        {activeGoals.length > 5 ? <p className="snapshot-goal-guidance"><Info />You have {activeGoals.length} active goals. Consider pausing one if the daily check-in starts to feel crowded.</p> : null}

        {currentGoals.length ? (
          <div className="snapshot-goal-groups">
            {dueGoals.length ? <section className="snapshot-goal-group snapshot-goal-group--due"><div className="snapshot-goal-group__heading"><h4><i aria-hidden="true" />Ready today</h4><span>Everything else stays folded below</span></div><div className="snapshot-goal-grid">{dueGoals.map((goal) => <GoalCardV2 key={goal.id} goal={goal} variant="due" onUpdate={openProgress} onQuickUpdate={quickUpdate} onQuickConfirmed={presentQuickConfirmation} onEdit={(selected) => setGoalEditor({ goal: selected, sourceKey: selected.source_question_key || undefined })} onStatus={(selected, status) => setLifecycle({ goal: selected, status })} onBuildOn={canCreateGoal ? (selected) => setGoalEditor({ goal: null, input: successorInput(selected) }) : undefined} />)}</div></section> : null}
            {otherGoals.length ? <section className="snapshot-goal-group snapshot-goal-group--ledger"><div className="snapshot-goal-group__heading"><h4>{dueGoals.length ? `Also tracking · ${otherGoals.length}` : `Tracking now · ${otherGoals.length}`}</h4><span>Nothing due — update any time</span></div><div className="snapshot-goal-grid">{otherGoals.map((goal) => <GoalCardV2 key={goal.id} goal={goal} variant="ledger" onUpdate={openProgress} onQuickUpdate={quickUpdate} onQuickConfirmed={presentQuickConfirmation} onEdit={(selected) => setGoalEditor({ goal: selected, sourceKey: selected.source_question_key || undefined })} onStatus={(selected, status) => setLifecycle({ goal: selected, status })} onBuildOn={canCreateGoal ? (selected) => setGoalEditor({ goal: null, input: successorInput(selected) }) : undefined} />)}</div></section> : null}
          </div>
        ) : canCreateGoal ? <div className="snapshot-empty"><p>No active goals yet. Add one when you are ready to begin tracking something concrete.</p><Button type="button" size="auto" className="mt-4 min-h-11" onClick={() => setGoalEditor({ goal: null })}>Add your first goal</Button></div> : null}

        {untrackedGoalSources.length ? (
          <section className="snapshot-goal-ideas" aria-labelledby="snapshot-life-plan-goal-ideas">
            <div><h4 id="snapshot-life-plan-goal-ideas">Goal ideas from your Life Plan</h4><p>These reflections are not tracked until you confirm a plan.</p></div>
            <div className="snapshot-goal-ideas__list">
              {untrackedGoalSources.map((sourceKey) => {
                const sourceIndex = ['snapshot_habit1','snapshot_habit2','snapshot_habit3'].indexOf(sourceKey);
                return <GoalSourceCard key={sourceKey} source={content[sourceKey]} index={sourceIndex} suggesting={suggesting === sourceKey} suggestionError={suggestionError === sourceKey} onSuggest={() => suggest(sourceKey)} onManual={() => setGoalEditor({ goal: null, sourceKey })} />;
              })}
            </div>
          </section>
        ) : null}

        {completedGoals.length ? (
          <details className="snapshot-goal-history">
            <summary><span>Completed goal history</span><span>{completedGoals.length} completed<ChevronDown /></span></summary>
            <div className="snapshot-completed-goals">
              {completedGoals.map((goal) => <CompletedGoalRow key={goal.id} goal={goal} onStatus={(selected, status) => setLifecycle({ goal: selected, status })} onBuildOn={canCreateGoal ? (selected) => setGoalEditor({ goal: null, input: successorInput(selected) }) : undefined} />)}
            </div>
          </details>
        ) : null}
      </div>

      <section className="snapshot-supporting" aria-labelledby="snapshot-support-title">
        <div className="snapshot-support-title"><h3 id="snapshot-support-title">Your supporting system</h3><p><span className="snapshot-support-title__desktop">The conditions that make the goals possible</span><span className="snapshot-support-title__mobile">One at a time</span></p></div>
        <div className="snapshot-support__grid">{supportItems.map((support, index) => { const open = supportOpen === index; if (!snapshotItemUnlocked(support.item)) return <SnapshotLock key={support.id} id={support.id} title={support.title} />; return <article id={support.id} key={support.id} className={open ? 'is-open' : ''}><button type="button" aria-expanded={open} onClick={() => setSupportOpen(open ? null : index)}><span><strong>{support.title}</strong><small>{support.hint}</small></span><ChevronDown aria-hidden="true" /></button>{open ? <div className="snapshot-support__body"><p>{support.list?.length ? support.list.join(' · ') : support.body || support.empty}</p></div> : null}</article>; })}</div>
      </section>
    </section>

    {quickConfirmation ? (
      <aside className="snapshot-quick-confirmation" data-testid="snapshot-quick-confirmation" role="status" aria-live="polite">
        <span className="snapshot-quick-confirmation__check" aria-hidden="true"><Check /></span>
        <div>
          <strong>Goal progress updated</strong>
          <p>{quickConfirmation.message}</p>
          {quickConfirmationError ? <small role="alert">{quickConfirmationError}</small> : null}
        </div>
        {quickConfirmation.undo ? <Button type="button" variant="ghost" size="auto" disabled={quickConfirmationUndoing} onClick={() => { void undoQuickConfirmation(); }}><RotateCcw />{quickConfirmationUndoing ? 'Undoing…' : 'Undo'}</Button> : null}
        <button type="button" className="snapshot-quick-confirmation__close" aria-label="Dismiss goal update confirmation" onClick={dismissQuickConfirmation}><X /></button>
      </aside>
    ) : null}
    <SnapshotContentEditor item={editingContent && snapshotItemUnlocked(content[editingContent.key]) ? editingContent : null} initialChallengeIndex={challengeOpen} onOpenChange={(open) => { if (!open) setEditingContent(null); }} onSaved={saveContent} />
    <GoalProgressModal goal={progressGoal} initialMode={progressMode} initialConfirmation={progressInitialConfirmation} initialWeeklySeal={Boolean(progressInitialConfirmation)} onOpenChange={(open) => { if (!open) closeProgress(); }} onSaved={saveProgress} onDone={closeProgress} onEditGoal={(goal) => { closeProgress(); setGoalEditor({ goal, sourceKey: goal.source_question_key || undefined }); }} />
    <GoalPlanEditor open={Boolean(goalEditor && (goalEditor.goal || (canCreateGoal && (!goalEditor.sourceKey || snapshotItemUnlocked(content[goalEditor.sourceKey])))))} goal={goalEditor?.goal || null} sourceQuestionKey={goalEditor?.sourceKey} initialInput={goalEditor?.input} userTimezone={data.season.timezone} onOpenChange={(open) => { if (!open) setGoalEditor(null); }} onSaved={saveGoal} />
    <GoalLifecycleModal goal={lifecycle?.goal || null} targetStatus={lifecycle?.status || null} onOpenChange={(open) => { if (!open) setLifecycle(null); }} onSaved={async (saved) => { if (saved.status === 'completed') setCompletedGoalPrompt(saved); setLifecycle(null); await load(); }} />
  </main>;
};

export default Snapshot;
